import { useState, useEffect, useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { selectIsMaintenanceMode, setMaintenanceMode } from '../redux/appState/appState';
import { ENVIRONMENT_URL } from '../settings';
import { fetchWrapper } from '../utils/fetch/fetchWrapper';

/**
 * Custom Hook for grow-ui to manage application health checks.
 * - Performs an initial health check using fetchWrapper on mount.
 * - If the initial check is OK, ensures maintenance mode is OFF in Redux.
 * - Provides a function to manually retry the health check.
 * - Returns the current maintenance status and the loading state of checks.
 * @returns {{ isMaintenanceMode: boolean; isChecking: boolean; retryCheck: () => Promise<void>; }}
 */
export const useMaintenanceModeCheck = () => {
  const dispatch = useDispatch();
  const isMaintenanceMode = useSelector(selectIsMaintenanceMode);
  const [isChecking, setIsChecking] = useState(!isMaintenanceMode);

  const performCheck = useCallback(async () => {
    const isActive = true;

    setIsChecking(true);
    console.log('Performing health check (via hook)...');

    try {
      const response = await fetchWrapper(`${ENVIRONMENT_URL}/health`);

      if (!isActive) return;

      if (response.ok) {
        console.log('Health check OK.');
        if (isMaintenanceMode) {
          console.log('HealthCheck hook found server OK, dispatching maintenance OFF.');
          dispatch(setMaintenanceMode(false));
        }
      } else {
        console.warn(`Health check received non-OK status: ${response.status}`);
        // fetchWrapper now handles emitting the event for 503 maintenance
      }
    } catch (error) {
      if (!isActive) return;

      console.error('Health check failed (network/other):', error);
    } finally {
      if (isActive) {
        setIsChecking(false);
      }
    }
  }, [dispatch, isMaintenanceMode]);

  useEffect(() => {
    if (!isMaintenanceMode) {
      performCheck();
    } else {
      if (isChecking) {
        setIsChecking(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performCheck]);

  const retryCheck = useCallback(async () => {
    await performCheck();
  }, [performCheck]);

  return { isMaintenanceMode, isChecking, retryCheck };
};
